import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useQuery } from "@tanstack/react-query";
import { extractContentFromFile } from "@/utils/universalContentExtractor";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Loader2 } from "lucide-react";

const formSchema = z.object({
  title: z.string().min(3, "Title must be at least 3 characters"),
  description: z.string().min(10, "Description must be at least 10 characters"),
  agency: z.string().min(1, "Please select an agency"),
  category: z.string().min(1, "Please select a category"),
  commercial_type: z.enum(["Sale", "Lease", "Collaborate", "Stake"]),
  base_price: z.number().min(0, "Price must be positive")
});

interface ManualSolutionCreationProps {
  onCancel: () => void;
}

export const ManualSolutionCreation = ({ onCancel }: ManualSolutionCreationProps) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: "",
      description: "",
      agency: "",
      category: "",
      commercial_type: "Sale",
      base_price: 0
    }
  });

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      setFile(selectedFile);
      
      // Auto-extract metadata from document
      await extractMetadata(selectedFile);
    }
  };

  const extractMetadata = async (file: File) => {
    setExtracting(true);
    try {
      // Read file content
      const text = await readFileAsText(file);
      
      // Call edge function to extract metadata
      const { data, error } = await supabase.functions.invoke('parse-document-metadata', {
        body: { 
          fileContent: text,
          fileName: file.name
        }
      });

      if (error) throw error;

      if (data?.title && !form.getValues('title')) {
        form.setValue('title', data.title);
      }
      if (data?.description && !form.getValues('description')) {
        form.setValue('description', data.description);
      }

      toast({
        title: "Metadata extracted",
        description: "Title and description have been auto-populated from your document"
      });
    } catch (error: any) {
      console.error('Metadata extraction error:', error);
      // Don't show error toast - just fall back to manual entry
    } finally {
      setExtracting(false);
    }
  };

  const readFileAsText = async (file: File): Promise<string> => {
    const { content } = await extractContentFromFile(file);
    return content;
  };

  const handleSubmit = async (values: z.infer<typeof formSchema>) => {
    if (step < 2) {
      setStep(step + 1);
      return;
    }

    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please log in to create a solution",
        variant: "destructive"
      });
      navigate('/auth');
      return;
    }

    setUploading(true);

    try {
      let fileUrl = null;
      let fileName = null;
      let fileSize = null;
      let fileType = null;

      // Upload file if provided
      if (file) {
        const fileExt = file.name.split('.').pop();
        const uploadFileName = `${user.id}/${Date.now()}.${fileExt}`;
        
        const { error: uploadError } = await supabase.storage
          .from('solutions')
          .upload(uploadFileName, file);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('solutions')
          .getPublicUrl(uploadFileName);

        fileUrl = publicUrl;
        fileName = file.name;
        fileSize = file.size;
        fileType = file.type;
      }

      // Calculate price based on commercial type
      const priceFactors = {
        Sale: 1.0,
        Lease: 0.1,
        Collaborate: 0.2,
        Stake: 0.5
      };
      const calculatedPrice = values.base_price * priceFactors[values.commercial_type];

      // Create solution
      const { data: solution, error: insertError } = await supabase
        .from('solutions')
        .insert({
          creator_id: user.id,
          title: values.title,
          description: values.description,
          agency: values.agency,
          category: values.category,
          commercial_type: values.commercial_type,
          base_price: values.base_price,
          price: calculatedPrice,
          status: 'published',
          file_url: fileUrl,
          file_name: fileName,
          file_size: fileSize,
          file_type: fileType
        })
        .select()
        .single();

      if (insertError) throw insertError;

      toast({
        title: "Solution Created! 🎉",
        description: "Your solution has been published successfully"
      });

      navigate('/myspai-profile');
    } catch (error: any) {
      console.error('Creation error:', error);
      toast({
        title: "Creation failed",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  const progressPercentage = (step / 2) * 100;

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium">Step {step} of 2</span>
          <span className="text-muted-foreground">{progressPercentage.toFixed(0)}% Complete</span>
        </div>
        <Progress value={progressPercentage} className="h-2" />
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
          {step === 1 && (
            <div className="space-y-6">
              <div className="text-center mb-4">
                <h3 className="text-2xl font-sans font-bold mb-2">Solution Details</h3>
                <p className="text-muted-foreground">Describe your solution</p>
              </div>

              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-lg">Title</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g., Advanced Procurement System for Federal Agencies" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-lg">Description</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Describe your solution, its benefits, and key features..."
                        className="min-h-32"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="agency"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Agency</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select agency" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="GSA">GSA</SelectItem>
                          <SelectItem value="Education">Education</SelectItem>
                          <SelectItem value="NOAA">NOAA</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="category"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Category</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g., Procurement" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <div className="text-center mb-4">
                <h3 className="text-2xl font-sans font-bold mb-2">Pricing & Upload</h3>
                <p className="text-muted-foreground">Set pricing and upload supporting documents</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="commercial_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Commercial Type</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="Sale">Sale</SelectItem>
                          <SelectItem value="Lease">Lease</SelectItem>
                          <SelectItem value="Collaborate">Collaborate</SelectItem>
                          <SelectItem value="Stake">Stake</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="base_price"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Base Price ($)</FormLabel>
                      <FormControl>
                        <Input 
                          type="number" 
                          placeholder="0" 
                          {...field}
                          onChange={e => field.onChange(parseFloat(e.target.value) || 0)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="border-2 border-dashed rounded-lg p-6">
                <div className="text-center">
                  <p className="text-sm text-muted-foreground mb-4">
                    Upload supporting documents (optional)
                  </p>
                  <Input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileChange}
                    className="max-w-xs mx-auto"
                    disabled={extracting}
                  />
                  {extracting && (
                    <p className="text-sm mt-2 text-blue-600 flex items-center justify-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Extracting title and description...
                    </p>
                  )}
                  {file && !extracting && (
                    <p className="text-sm mt-2 text-green-600">Selected: {file.name}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-between pt-4">
            <Button type="button" variant="outline" onClick={step === 1 ? onCancel : () => setStep(1)}>
              {step === 1 ? "Cancel" : "Back"}
            </Button>
            <Button type="submit" disabled={uploading}>
              {uploading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : step === 1 ? (
                "Next"
              ) : (
                "Create Solution"
              )}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};
